import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import { AppDispatch, RootState } from '../../store';
import {
  createPaymentRequest,
  clearPaymentState,
  customerPortalRequest,
  getCurrentPlanRequest,
  selectCurrentPlan,
  selectCurrentPlanLoading,
  selectCustomerPortalError,
  selectCustomerPortalLoading,
} from '../../store/payments';
import { normalizeSubscriptionStatus } from '../../services/paymentService/paymentService';
import { useToast } from '../../contexts/ToastContext';

type PlanTier = {
  id: string;
  name: string;
  description: string;
  priceLabel: string;
  periodLabel?: string;
  annualBillingLabel?: string;
  equivalentLabel?: string;
  features: string[];
  highlighted?: boolean;
  ctaLabel: string;
};

const PLANS: PlanTier[] = [
  {
    id: 'free',
    name: 'Free',
    description:
      'Perfect for solo founders testing product-market fit and exploring email automation.',
    priceLabel: '$0',
    features: [
      'Up to 100 emails / month',
      'Up to 500 contacts',
      'Smart Segments with AND/OR Logic',
      'Visual Workflow Builder',
      'Drag & Drop Email Editor',
      'Basic Campaign Analytics',
      '1 Domain',
    ],
    ctaLabel: 'Free plan',
  },
  {
    id: 'pro',
    name: 'Pro',
    description:
      'For early-stage SaaS teams building their first onboarding and nurture campaigns.',
    priceLabel: '$29',
    features: [
      '5,000 emails / month',
      '10,000 contacts',
      'Unlimited Smart Segments',
      'Conversion Goal Tracking',
      '3 Team Members',
      'Full REST API Access',
      '2 Domain',
    ],
    highlighted: true,
    ctaLabel: 'Upgrade to Pro',
  },
  {
    id: 'business',
    name: 'Business',
    description:
      'For scaling SaaS teams running lifecycle campaigns across multiple user segments.',
    priceLabel: '$59',
    features: [
      '25,000 emails / month',
      '50,000 contacts',
      'Unlimited Campaigns & Workflows',
      'Advanced Analytics & Reporting',
      '10 Team Members',
      'Priority Email Support',
      '5 Domain',
    ],
    highlighted: false,
    ctaLabel: 'Upgrade to Business',
  },
];

const YEARLY_PLANS: PlanTier[] = [
  {
    id: 'free',
    name: 'Free',
    description:
      'Perfect for solo founders testing product-market fit and exploring email automation.',
    priceLabel: '$0',
    features: [
      'Up to 100 emails / month',
      'Up to 500 contacts',
      'Smart Segments with AND/OR Logic',
      'Visual Workflow Builder',
      'Drag & Drop Email Editor',
      'Basic Campaign Analytics',
      '1 Domain',
    ],
    ctaLabel: 'Free plan',
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For early-stage SaaS shipping first campaigns.',
    priceLabel: '$19',
    periodLabel: '/month',
    annualBillingLabel: '$228/year billed annually',
    equivalentLabel: 'Customer.io equivalent: $100/mo',
    features: [
      '5,000 contacts',
      '10,000 emails/month',
      'Unlimited smart segments',
      'Conversion goal tracking',
      '3 team members',
      'Full REST API access',
    ],
    highlighted: true,
    ctaLabel: 'Upgrade to Pro',
  },
  {
    id: 'business',
    name: 'Business',
    description: 'For scaling SaaS running lifecycle campaigns.',
    priceLabel: '$49',
    periodLabel: '/month',
    annualBillingLabel: '$588/year billed annually',
    equivalentLabel: 'Customer.io equivalent: $200/mo',
    features: [
      '25,000 contacts',
      '50,000 emails/month',
      'Unlimited campaigns & workflows',
      'Advanced analytics & reporting',
      '10 team members',
      'Priority email support',
    ],
    highlighted: false,
    ctaLabel: 'Upgrade to Business',
  },
];

function billingTierIdFromPlanName(
  name: string | null | undefined
): 'free' | 'pro' | 'business' | null {
  if (!name?.trim()) return null;
  const n = name.trim().toLowerCase();
  if (n === 'free' || n === 'starter') return 'free';
  if (n === 'pro' || n === 'pro-yearly' || n === 'pro yearly') return 'pro';
  if (n === 'business' || n === 'business-yearly' || n === 'business yearly')
    return 'business';
  return null;
}

/** True when this pricing card matches `payments/current-plan` `plan` (tier id or name). */
function planCardMatchesApiResponse(
  plan: PlanTier,
  apiPlanName: string | undefined,
  tierId: ReturnType<typeof billingTierIdFromPlanName>
): boolean {
  const name = apiPlanName?.trim();
  if (!name) {
    return plan.id === 'free';
  }
  if (tierId !== null) {
    return tierId === plan.id;
  }
  const low = name.toLowerCase();
  return plan.id === low || plan.name.toLowerCase() === low;
}

const UpgradePlans: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
    'monthly'
  );
  const dispatch = useDispatch<AppDispatch>();
  const {
    createPaymentLoading,
    selectedProduct,
    createPaymentError,
    createPaymentMessage,
    createPaymentChangeType,
  } = useSelector((state: RootState) => state.payments);
  const currentPlan = useSelector(selectCurrentPlan);
  const currentPlanLoading = useSelector(selectCurrentPlanLoading);
  const customerPortalLoading = useSelector(selectCustomerPortalLoading);
  const customerPortalError = useSelector(selectCustomerPortalError);
  const { showToast } = useToast();

  useEffect(() => {
    dispatch(getCurrentPlanRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!createPaymentError) {
      return;
    }

    showToast({
      type: 'error',
      title: 'Payment Failed',
      message: createPaymentError,
    });
    dispatch(clearPaymentState());
  }, [createPaymentError, dispatch, showToast]);

  useEffect(() => {
    if (!customerPortalError) {
      return;
    }
    showToast({
      type: 'error',
      title: 'Billing portal',
      message: customerPortalError,
    });
  }, [customerPortalError, showToast]);

  useEffect(() => {
    if (!createPaymentMessage || !createPaymentChangeType) {
      return;
    }

    const isDowngrade = createPaymentChangeType.toLowerCase() === 'downgrade';

    showToast({
      type: 'success',
      title: isDowngrade ? 'Plan Downgraded' : 'Plan Updated',
      message: createPaymentMessage,
    });
    dispatch(clearPaymentState());
  }, [createPaymentChangeType, createPaymentMessage, dispatch, showToast]);

  const handlePlanCheckout = (product: string) => {
    const checkoutProduct =
      billingCycle === 'yearly' && (product === 'pro' || product === 'business')
        ? `${product}-yearly`
        : product;
    dispatch(createPaymentRequest(checkoutProduct));
  };

  const apiPlanName = currentPlan?.plan?.trim();
  const currentTierId = billingTierIdFromPlanName(apiPlanName);
  const subscriptionStatus = normalizeSubscriptionStatus(currentPlan?.status);
  const isPlanProcessing = subscriptionStatus === 'processing';
  const isPlanOnHold = subscriptionStatus === 'on_hold';
  const selectedPlanLabel =
    !currentPlanLoading && apiPlanName
      ? apiPlanName
      : !currentPlanLoading
        ? 'Free'
        : null;
  const isPremiumPlanBadge =
    !currentPlanLoading &&
    apiPlanName != null &&
    apiPlanName.length > 0 &&
    apiPlanName.toLowerCase() !== 'free';
  const plansToRender = useMemo(
    () => (billingCycle === 'yearly' ? YEARLY_PLANS : PLANS),
    [billingCycle]
  );

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-background text-zinc-900 antialiased">
      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-4 pt-10 pb-2">
        <Link
          to="/account-settings"
          className="mb-10 inline-flex w-fit items-center gap-2 text-sm font-medium text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to account settings
        </Link>

        <header className="mx-auto mb-14 max-w-2xl text-center sm:mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Pricing
          </p>
          <h1 className="mt-3 text-balance text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl sm:leading-tight">
            Plans that scale with your team
          </h1>
          <p className="mt-4 text-pretty text-base leading-relaxed text-zinc-600 sm:text-lg">
            Simple limits for email, contacts, and segments. Upgrade when you
            need more room to grow.
          </p>
          <div className="mt-6 inline-flex rounded-xl border border-zinc-200 bg-zinc-100/70 p-1">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                billingCycle === 'monthly'
                  ? 'bg-white text-zinc-950 shadow-sm'
                  : 'text-zinc-600 hover:text-zinc-900'
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                billingCycle === 'yearly'
                  ? 'bg-white text-zinc-950 shadow-sm'
                  : 'text-zinc-600 hover:text-zinc-900'
              )}
            >
              Yearly
            </button>
          </div>
          {billingCycle === 'yearly' && (
            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Save 20% with annual billing
            </p>
          )}
          {selectedPlanLabel && (
            <p className="mt-4 text-sm font-medium text-zinc-800">
              Your plan:{' '}
              <span
                className={cn(
                  isPremiumPlanBadge
                    ? 'inline-flex items-center gap-1.5 rounded-full border border-amber-300/80 bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-50 px-3.5 py-1.5 text-sm font-semibold text-amber-950 shadow-md shadow-amber-900/10 ring-1 ring-amber-400/40'
                    : 'font-semibold text-zinc-950'
                )}
              >
                {isPremiumPlanBadge && (
                  <Sparkles
                    className="h-3.5 w-3.5 shrink-0 text-amber-600"
                    aria-hidden
                  />
                )}
                {selectedPlanLabel}
              </span>
            </p>
          )}
          {!currentPlanLoading && isPlanProcessing && (
            <p className="mt-3 max-w-xl mx-auto text-sm leading-relaxed text-amber-900">
              Your plan is processing and should be fully active within 24
              hours.
            </p>
          )}
        </header>

        <div className="mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 items-stretch gap-6 lg:grid-cols-3 lg:gap-5 lg:items-start">
          {plansToRender.map((plan) => {
            const isPlanMatch = planCardMatchesApiResponse(
              plan,
              apiPlanName,
              currentTierId
            );
            const isSelected = isPlanMatch;
            let primaryLabel = plan.ctaLabel;
            if (isSelected) {
              if (isPlanProcessing) {
                primaryLabel = 'Activates within 24 hours';
              } else if (isPlanOnHold) {
                primaryLabel = 'On hold';
              } else {
                primaryLabel = 'Current plan';
              }
            }
            return (
              <article
                key={plan.name}
                className={cn(
                  'relative flex flex-col rounded-3xl border bg-white/80 p-7 shadow-sm backdrop-blur-sm transition-all duration-300 sm:p-8',
                  plan.highlighted
                    ? 'border-zinc-900 shadow-xl shadow-zinc-900/[0.08] ring-1 ring-zinc-900 lg:z-10 lg:-mt-2 lg:scale-[1.02]'
                    : 'border-zinc-200/90 hover:border-zinc-300 hover:shadow-md',
                  isSelected &&
                    'ring-2 ring-emerald-600/90 border-emerald-600/40 shadow-md'
                )}
              >
                {isSelected && (
                  <div className="absolute -top-3 right-4 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
                    Selected
                  </div>
                )}
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-zinc-900 px-3 py-1 text-xs font-semibold text-white shadow-md">
                    <Sparkles className="h-3 w-3" aria-hidden />
                    Most popular
                  </div>
                )}

                <div
                  className={cn(
                    'mb-6 h-1 w-10 rounded-full',
                    plan.highlighted ? 'bg-zinc-900' : 'bg-zinc-200'
                  )}
                  aria-hidden
                />

                <div className="mb-6">
                  <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
                    {plan.name}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600">
                    {plan.description}
                  </p>
                  <div className="mt-6 flex items-baseline gap-1 border-t border-zinc-100 pt-6">
                    <span className="text-4xl font-semibold tracking-tight text-zinc-950 tabular-nums">
                      {plan.priceLabel}
                    </span>
                    {plan.priceLabel !== 'Custom' && (
                      <span className="text-sm font-medium text-zinc-500">
                        {plan.periodLabel ?? '/month'}
                      </span>
                    )}
                  </div>
                  {plan.annualBillingLabel && (
                    <p className="mt-2 text-sm font-medium text-zinc-700">
                      {plan.annualBillingLabel}
                    </p>
                  )}
                  {plan.equivalentLabel && (
                    <p className="mt-1 text-xs text-zinc-500">
                      {plan.equivalentLabel}
                    </p>
                  )}
                </div>

                <ul className="mb-8 flex flex-1 flex-col gap-3.5 text-sm leading-snug text-zinc-700">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                        <Check className="h-3 w-3 stroke-[3]" aria-hidden />
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex w-full flex-col gap-2">
                  {isPlanOnHold && isPlanMatch && (
                    <Button
                      type="button"
                      variant="outline"
                      size="medium"
                      className="w-full rounded-xl border-red-200 py-2.5 font-semibold text-red-700 hover:bg-red-50"
                      disabled={customerPortalLoading}
                      onClick={() => dispatch(customerPortalRequest())}
                    >
                      {customerPortalLoading ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Opening…
                        </>
                      ) : (
                        'Revoke subscription'
                      )}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={plan.highlighted ? 'primary' : 'outline'}
                    size="medium"
                    className={cn(
                      'w-full rounded-xl py-2.5 font-semibold',
                      !plan.highlighted &&
                        'border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50'
                    )}
                    disabled={
                      plan.id === 'free' || createPaymentLoading || isSelected
                    }
                    onClick={() => handlePlanCheckout(plan.id)}
                  >
                    {createPaymentLoading && selectedProduct === plan.id ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                        Redirecting...
                      </>
                    ) : (
                      primaryLabel
                    )}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mx-auto mt-4 max-w-lg text-center text-xs leading-relaxed text-zinc-500">
          Prices shown are illustrative. Billing and checkout will connect to
          your payment provider when available.
        </p>
      </div>
    </div>
  );
};

export default UpgradePlans;
